'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { User, Mail, Phone, CreditCard, MapPin, Lock, Eye, EyeOff, TrendingDown, Zap, FileText, HelpCircle, UserPlus, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { GoogleLogin } from '@react-oauth/google';
import { BRAZILIAN_STATES } from '@/lib/constants';
import GeolocationModal from '@/components/ui/GeolocationModal';
import { useAuth } from '@/contexts/AuthContext';
import { maskPhoneInput, unmaskPhone } from '@/utils/phone';


interface RegisterForm {
  fullName: string;
  email: string;
  phone: string;
  cpfCnpj: string;
  state: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
  role: string;
}

interface LoginForm {
  email: string;
  password: string;
}

const benefits = [
  {
    id: 'benefit-costs',
    icon: TrendingDown,
    title: 'Reduza seus custos',
    desc: 'Simulações que identificam as melhores oportunidades de economia.',
  },
  {
    id: 'benefit-strategies',
    icon: Zap,
    title: 'Estratégias inteligentes',
    desc: 'Time shifting, peak shaving e mais para otimizar sua operação.',
  },
  {
    id: 'benefit-reports',
    icon: FileText,
    title: 'Relatórios completos',
    desc: 'Relatórios detalhados e personalizados para apoiar suas decisões.',
  },
];

export default function SignUpLoginContent() {
  const [mode, setMode] = useState<'register' | 'login'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showGeoModal, setShowGeoModal] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const { signIn, processGoogleLogin, registerWithBearsAPI } = useAuth();

  const isGoogleRegistration = searchParams.get('google') === 'true';

  const {
    register: registerField,
    handleSubmit,
    watch,
    setValue: setRegisterValue,
    formState: { errors },
  } = useForm<RegisterForm>();

  React.useEffect(() => {
    if (searchParams.get('google') === 'true') {
      setMode('register');
      let email = searchParams.get('email');
      let name = searchParams.get('name');
      if (email) setRegisterValue('email', email);
      if (name) setRegisterValue('fullName', name);
    }
  }, [searchParams, setRegisterValue]);

  const getGeolocationSilently = (): Promise<{ lat: number; lng: number } | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = parseFloat(position.coords.latitude.toFixed(6));
          const lng = parseFloat(position.coords.longitude.toFixed(6));
          localStorage.setItem('user_lat', lat.toString());
          localStorage.setItem('user_lng', lng.toString());
          resolve({ lat, lng });
        },
        () => resolve(null)
      );
    });
  };

  const {
    register: loginRegister,
    handleSubmit: loginHandleSubmit,
    formState: { errors: loginErrors },
  } = useForm<LoginForm>();

  const passwordValue = watch('password');

  const passwordRules = [
    {
      id: 'minLength',
      label: 'Pelo menos 8 caracteres',
      test: (v: string) => v.length >= 8,
    },
    {
      id: 'uppercase',
      label: 'Pelo menos uma letra maiúscula',
      test: (v: string) => /[A-Z]/.test(v),
    },
    {
      id: 'lowercase',
      label: 'Pelo menos uma letra minúscula',
      test: (v: string) => /[a-z]/.test(v),
    },
    {
      id: 'number',
      label: 'Pelo menos um número',
      test: (v: string) => /[0-9]/.test(v),
    },
    {
      id: 'special',
      label: 'Pelo menos um caractere especial',
      test: (v: string) => /[^A-Za-z0-9]/.test(v),
    },
  ];

  const onRegister = async (data: RegisterForm) => {
    setIsLoading(true);
    try {
      const coords = await getGeolocationSilently();
      const authProvider = searchParams.get('google') === 'true' ? 'GOOGLE' : 'EMAIL';

      let providerId = searchParams.get('providerId') || null;

      const payload = {
        fullName: data.fullName,
        email: data.email,
        phone: data.phone ? unmaskPhone(data.phone) : null,
        documentType: data.cpfCnpj.length > 14 ? 'CNPJ' : 'CPF',
        documentNumber: data.cpfCnpj.replace(/\D/g, ''),
        stateCode: data.state,
        password: data.password,
        authProvider,
        providerId,
        latitude: coords?.lat ?? null,
        longitude: coords?.lng ?? null,
        role: data.role,
      };

      await registerWithBearsAPI(payload);

      if (authProvider === 'EMAIL') {
        // Sign in right after registration to acquire the session cookie
        await signIn(data.email, data.password, coords?.lat ?? null, coords?.lng ?? null);
      }

      toast.success('Conta criada com sucesso! Bem-vindo ao BEARS Energy.');
      if (!coords) {
        setShowGeoModal(true);
      } else {
        const resolvedRole =
          typeof document !== 'undefined'
            ? document.cookie
                .split('; ')
                .find((row) => row.startsWith('bears_user_role='))
                ?.split('=')[1] || 'integrator'
            : 'integrator';
        router.push(
          resolvedRole === 'admin' ? '/admin/configurations' : `/${resolvedRole}/dashboard`
        );
      }
    } catch (err: any) {
      if (err?.status === 409) {
        toast.error('Este e-mail já possui uma conta. Deseja fazer login?', {
          action: {
            label: 'Fazer Login',
            onClick: () => setMode('login'),
          },
        });
      } else {
        toast.error(err?.message ?? 'Erro ao criar conta. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onLogin = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      const coords = await getGeolocationSilently();
      await signIn(data.email, data.password, coords?.lat ?? null, coords?.lng ?? null);
      toast.success('Login realizado com sucesso!');
      if (!coords) {
        setShowGeoModal(true);
      } else {
        const resolvedRole =
          typeof document !== 'undefined'
            ? document.cookie
                .split('; ')
                .find((row) => row.startsWith('bears_user_role='))
                ?.split('=')[1] || 'integrator'
            : 'integrator';
        router.push(
          resolvedRole === 'admin' ? '/admin/configurations' : `/${resolvedRole}/dashboard`
        );
      }
    } catch (err: any) {
      const message = err?.message ?? '';

      // Network / fetch failure
      if (
        message.toLowerCase().includes('failed to fetch') ||
        message.toLowerCase().includes('network') ||
        message.toLowerCase().includes('fetch')
      ) {
        toast.error('Sem conexão com o servidor. Verifique sua internet e tente novamente.');
      } else {
        // Use the already-mapped message from AuthContext (or a safe fallback)
        toast.error(message || 'Credenciais inválidas. Verifique seu e-mail e senha.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    if (!credentialResponse.credential) return;
    setIsGoogleLoading(true);
    try {
      await processGoogleLogin(credentialResponse.credential);
      toast.success('Login com Google realizado com sucesso!');
      const resolvedRole =
        typeof document !== 'undefined'
          ? document.cookie
              .split('; ')
              .find((row) => row.startsWith('bears_user_role='))
              ?.split('=')[1] || 'integrator'
          : 'integrator';
      router.push(
        resolvedRole === 'admin' ? '/admin/configurations' : `/${resolvedRole}/dashboard`
      );
    } catch (err: any) {
      const errorMessage = err?.message || '';
      const lowerMsg = errorMessage.toLowerCase();

      if (
        err?.status === 404 ||
        lowerMsg.includes('cadastro') ||
        lowerMsg.includes('redirecion') ||
        lowerMsg.includes('não encontrado') ||
        lowerMsg.includes('nao encontrado') ||
        lowerMsg.includes('not found')
      ) {
        let email = '';
        let name = '';
        let providerId = '';
        try {
          const base64Url = credentialResponse.credential.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
            window
              .atob(base64)
              .split('')
              .map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
              })
              .join('')
          );
          const payload = JSON.parse(jsonPayload);
          email = payload.email || '';
          name = payload.name || '';
          providerId = payload.sub || '';
        } catch (e) {
          console.error('Failed to decode Google token', e);
        }

        router.push(
          `${window.location.pathname}?google=true&email=${encodeURIComponent(email)}&name=${encodeURIComponent(name)}&providerId=${encodeURIComponent(providerId)}`
        );
      } else {
        toast.error(errorMessage || 'Erro ao entrar com Google.');
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleGeoClose = (coords?: { lat: number; lng: number }) => {
    setShowGeoModal(false);
    if (coords) {
      localStorage.setItem('user_lat', coords.lat.toString());
      localStorage.setItem('user_lng', coords.lng.toString());
      toast.success(`Localização salva: ${coords.lat}, ${coords.lng}`);
    }
    const resolvedRole =
      typeof document !== 'undefined'
        ? document.cookie
            .split('; ')
            .find((row) => row.startsWith('bears_user_role='))
            ?.split('=')[1] || 'integrator'
        : 'integrator';
    router.push(resolvedRole === 'admin' ? '/admin/configurations' : `/${resolvedRole}/dashboard`);
  };

  return (
    <>
      {showGeoModal && <GeolocationModal onClose={handleGeoClose} />}

      <div className="h-screen bg-background flex flex-col md:flex-row overflow-hidden">
        {/* Left Column */}
        <div className="hidden md:flex flex-col w-full md:w-[48%] lg:w-[52%] h-full relative overflow-hidden bg-white">
          {/* Header with original uncropped logo (with bear) side-by-side with title */}
          <div className="px-8 lg:px-10 pt-4 pb-4">
            <div className="flex items-center gap-5">
              <img
                src="/assets/images/BEARS-Energy.svg"
                alt="BEARS Energy"
                className="w-[135px] lg:w-[145px] h-auto object-contain flex-shrink-0"
              />
              <h1 className="text-[1.35rem] lg:text-[1.65rem] font-extrabold text-bears-blue leading-tight flex-1">
                Simule projetos de BESS a partir da sua fatura de energia
              </h1>
            </div>
          </div>

          {/* Main content */}
          <div className="px-8 lg:px-10 flex-1">
            <p className="text-muted-foreground text-sm lg:text-base leading-relaxed mb-6 lg:mb-8 max-w-md">
              Análise inteligente, simulações precisas e estratégias personalizadas para reduzir
              seus custos com energia.
            </p>

            <div className="flex flex-col gap-3 lg:gap-4 mb-6 lg:mb-8">
              {benefits.map((b) => {
                const BenefitIcon = b.icon;
                return (
                  <div key={b.id} className="flex items-start gap-3 lg:gap-4">
                    <div
                      className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #2CC4BE22, #104CAE22)' }}
                    >
                      <BenefitIcon size={16} className="text-battery-turquoise" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">{b.title}</p>
                      <p className="text-xs text-muted-foreground leading-snug">{b.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* BESS Image */}
          <div className="relative mx-8 lg:mx-10 mb-8 rounded-2xl overflow-hidden h-44 lg:h-52">
            <div className="absolute inset-0 bg-gradient-to-br from-bears-blue/90 via-battery-turquoise/60 to-transparent z-10" />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-secondary/40 z-10" />
            <img
              src="https://img.rocket.new/generatedImages/rocket_gen_img_161da9601-1770115699059.png"
              alt="BESS Industrial Energy Storage"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-4 left-4 right-4 z-20 text-white">
              <div className="inline-block bg-black/40 backdrop-blur-sm rounded-lg px-3 py-2">
                <p className="text-xs font-bold uppercase tracking-wider text-battery-turquoise mb-0.5" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
                  Tecnologia BESS
                </p>
                <p className="text-sm font-semibold" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
                  Armazenamento de Energia para Indústria e Comércio
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex-1 flex flex-col relative bg-background h-full overflow-y-auto">
          {/* Mobile logo — original uncropped logo side-by-side with title */}
          <div className="md:hidden flex items-center gap-4 pt-5 pb-4 px-6 bg-white border-b border-border">
            <img
              src="/assets/images/BEARS-Energy.svg"
              alt="BEARS Energy"
              className="w-[77px] h-auto object-contain flex-shrink-0"
            />
            <h1 className="text-base font-extrabold text-bears-blue leading-snug flex-1">
              Simule projetos de BESS a partir da sua fatura de energia
            </h1>
          </div>

          <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-6 sm:py-8">
            <div className="w-full max-w-md flex flex-col gap-5">
              <div className="card-base p-6 sm:p-8">
                {mode === 'register' ? (
                  <>
                    <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-1">
                      Crie sua conta
                    </h2>
                    <p className="text-sm text-muted-foreground mb-5">
                      Preencha os dados abaixo para começar.
                    </p>

                    <form
                      key="register-form"
                      onSubmit={handleSubmit(onRegister)}
                      className="flex flex-col gap-4"
                    >
                      {/* Full Name */}
                      <div>
                        <label className="label-text">Nome completo</label>
                        <div className="relative">
                          <User
                            size={15}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          />
                          <input
                            {...registerField('fullName', { required: 'Nome obrigatório' })}
                            type="text"
                            placeholder="Digite seu nome completo"
                            className="input-field pl-9"
                          />
                        </div>
                        {errors.fullName && <p className="error-text">{errors.fullName.message}</p>}
                      </div>

                      {/* Email */}
                      <div>
                        <label className="label-text">E-mail</label>
                        <div className="relative">
                          <Mail
                            size={15}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          />
                          <input
                            {...registerField('email', {
                              required: 'E-mail obrigatório',
                              pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: 'E-mail inválido',
                              },
                            })}
                            type="email"
                            readOnly={isGoogleRegistration}
                            placeholder="seu@email.com"
                            className={`input-field pl-9 ${isGoogleRegistration ? 'bg-muted text-muted-foreground' : ''}`}
                          />
                        </div>
                        {errors.email && <p className="error-text">{errors.email.message}</p>}
                      </div>

                      {/* Telefone */}
                      <div>
                        <label className="label-text">Telefone</label>
                        <div className="relative">
                          <Phone
                            size={15}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          />
                          <input
                            {...registerField('phone', {
                              onChange: (e) => {
                                setRegisterValue('phone', maskPhoneInput(e.target.value));
                              },
                            })}
                            type="tel"
                            placeholder="+55 (11) 999-999-999"
                            className="input-field pl-9"
                          />
                        </div>
                        {errors.phone && <p className="error-text">{errors.phone.message}</p>}
                      </div>

                      {/* CPF/CNPJ */}
                      <div>
                        <label className="label-text">CPF / CNPJ</label>
                        <div className="relative">
                          <CreditCard
                            size={15}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          />
                          <input
                            {...registerField('cpfCnpj', { required: 'CPF/CNPJ obrigatório' })}
                            type="text"
                            placeholder="000.000.000-00"
                            className="input-field pl-9"
                          />
                        </div>
                        {errors.cpfCnpj && <p className="error-text">{errors.cpfCnpj.message}</p>}
                      </div>

                      {/* State */}
                      <div>
                        <label className="label-text">Local de atuação</label>
                        <div className="relative">
                          <MapPin
                            size={15}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          />
                          <select
                            {...registerField('state', { required: 'Estado obrigatório' })}
                            className="input-field pl-9 appearance-none"
                          >
                            <option value="">Selecione seu estado</option>
                            {BRAZILIAN_STATES.map((s) => (
                              <option key={`state-${s}`} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </div>
                        {errors.state && <p className="error-text">{errors.state.message}</p>}
                      </div>

                      {/* Profile Selection */}
                      <div>
                        <label className="label-text">Perfil</label>
                        <div className="relative">
                          <User
                            size={15}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          />
                          <select
                            {...registerField('role', { required: 'Perfil obrigatório' })}
                            className="input-field pl-9 appearance-none"
                          >
                            {/* <option value="">Selecione seu perfil</option> */}
                            {/* <option value="distributor">Distribuidor</option> */}
                            <option value="integrator">Integrador</option>
                            {/* <option value="account-executive">Executivo de Contas</option> */}
                            {/* <option value="customer">Cliente</option> */}
                          </select>
                        </div>
                        {errors.role && <p className="error-text">{errors.role.message}</p>}
                      </div>

                      {!isGoogleRegistration && (
                        <>
                          {/* Password */}
                          <div>
                            <label className="label-text">Senha</label>
                            <div className="relative">
                              <Lock
                                size={15}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                              />
                              <input
                                {...registerField('password', {
                                  required: 'Senha obrigatória',
                                  minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                                  validate: (v) => {
                                    if (!/[A-Z]/.test(v)) return 'Inclua pelo menos uma letra maiúscula';
                                    if (!/[a-z]/.test(v)) return 'Inclua pelo menos uma letra minúscula';
                                    if (!/[0-9]/.test(v)) return 'Inclua pelo menos um número';
                                    if (!/[^A-Za-z0-9]/.test(v)) return 'Inclua pelo menos um caractere especial';
                                    return true;
                                  },
                                })}
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Crie uma senha segura"
                                className="input-field pl-9 pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                              >
                                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                              </button>
                            </div>
                            {errors.password && (
                              <p className="error-text">{errors.password.message}</p>
                            )}
                            {/* Password rules feedback */}
                            <div className="mt-2 flex flex-col gap-1">
                              {passwordRules.map((rule) => {
                                const passed = passwordValue ? rule.test(passwordValue) : false;
                                return (
                                  <div key={rule.id} className="flex items-center gap-1.5">
                                    <span
                                      className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors duration-200 ${
                                        passed
                                          ? 'bg-green-500 text-white' :'bg-muted text-muted-foreground border border-border'
                                      }`}
                                    >
                                      {passed ? '✓' : ''}
                                    </span>
                                    <span
                                      className={`text-xs transition-colors duration-200 ${
                                        passed ? 'text-green-600 font-medium' : 'text-muted-foreground'
                                      }`}
                                    >
                                      {rule.label}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Confirm Password */}
                          <div>
                            <label className="label-text">Confirmar senha</label>
                            <div className="relative">
                              <Lock
                                size={15}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                              />
                              <input
                                {...registerField('confirmPassword', {
                                  required: 'Confirmação obrigatória',
                                  validate: (v) => v === passwordValue || 'As senhas não coincidem',
                                })}
                                type={showConfirm ? 'text' : 'password'}
                                placeholder="Confirme sua senha"
                                className="input-field pl-9 pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowConfirm(!showConfirm)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                aria-label={showConfirm ? 'Ocultar senha' : 'Mostrar senha'}
                              >
                                {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                              </button>
                            </div>
                            {errors.confirmPassword && (
                              <p className="error-text">{errors.confirmPassword.message}</p>
                            )}
                          </div>
                        </>
                      )}

                      {/* Terms */}
                      <div>
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            {...registerField('terms', { required: 'Você deve aceitar os termos' })}
                            type="checkbox"
                            className="mt-0.5 accent-primary"
                          />
                          <span className="text-xs text-muted-foreground leading-snug">
                            Li e concordo com os{' '}
                            <a href="#" className="text-primary font-semibold hover:underline">
                              Termos de Uso
                            </a>{' '}
                            e{' '}
                            <a href="#" className="text-primary font-semibold hover:underline">
                              Política de Privacidade
                            </a>
                          </span>
                        </label>
                        {errors.terms && <p className="error-text">{errors.terms.message}</p>}
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="btn-primary w-full mt-1 py-3"
                      >
                        {isLoading ? (
                          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8z"
                            />
                          </svg>
                        ) : (
                          <>
                            <UserPlus size={16} />
                            Criar conta
                          </>
                        )}
                      </button>
                    </form>

                    <p className="text-center text-sm text-muted-foreground mt-4">
                      Já tem uma conta?{' '}
                      <button
                        onClick={() => setMode('login')}
                        className="text-primary font-bold hover:underline"
                      >
                        Faça login
                      </button>
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-1">
                      Entrar na plataforma
                    </h2>
                    <p className="text-sm text-muted-foreground mb-5">
                      Use suas credenciais para acessar.
                    </p>

                    <form
                      key="login-form"
                      onSubmit={loginHandleSubmit(onLogin)}
                      className="flex flex-col gap-4"
                    >
                      <div>
                        <label className="label-text">E-mail</label>
                        <div className="relative">
                          <Mail
                            size={15}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          />
                          <input
                            {...loginRegister('email', { required: 'E-mail obrigatório' })}
                            type="email"
                            placeholder="seu@email.com"
                            className="input-field pl-9"
                          />
                        </div>
                        {loginErrors.email && (
                          <p className="error-text">{loginErrors.email.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="label-text">Senha</label>
                        <div className="relative">
                          <Lock
                            size={15}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          />
                          <input
                            {...loginRegister('password', { required: 'Senha obrigatória' })}
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Sua senha"
                            className="input-field pl-9 pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          >
                            {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                          </button>
                        </div>
                        {loginErrors.password && (
                          <p className="error-text">{loginErrors.password.message}</p>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="btn-primary w-full py-3"
                      >
                        {isLoading ? (
                          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8z"
                            />
                          </svg>
                        ) : (
                          <>
                            <LogIn size={16} />
                            Entrar
                          </>
                        )}
                      </button>
                    </form>

                    {/* Divider */}
                    <div className="flex items-center gap-3 my-4">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-xs text-muted-foreground">ou continue com</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>

                    {/* Google Sign In */}
                    <div className="w-full relative min-h-[42px] flex items-center justify-center">
                      {isGoogleLoading && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 rounded-lg">
                          <svg
                            className="animate-spin w-5 h-5 text-primary"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8z"
                            />
                          </svg>
                        </div>
                      )}
                      <div className="w-full flex justify-center">
                        <GoogleLogin
                          onSuccess={handleGoogleSuccess}
                          onError={() => toast.error('Ocorreu um erro no provedor do Google.')}
                          shape="rectangular"
                          text="continue_with"
                          logo_alignment="center"
                        />
                      </div>
                    </div>

                    <p className="text-center text-sm text-muted-foreground mt-4">
                      Não tem conta?{' '}
                      <button
                        onClick={() => setMode('register')}
                        className="text-primary font-bold hover:underline"
                      >
                        Criar conta gratis
                      </button>
                    </p>
                  </>
                )}
              </div>

              {/* Help link — centered below the form card */}
              <div className="flex justify-center pb-2">
                <button className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                  <HelpCircle size={16} />
                  Precisa de ajuda?
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
